// src/SpecsSection.js
import React, { useState, useEffect, useRef } from 'react';
import './SpecsSection.css';
import { authFetch } from './utils/api'; // adjust path if needed
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getMatchedPromo } from './utils/promoMatcher';
import { getTemplates } from './docs/getTemplates';

// Cache for appearance-templates (per product type)
const templateCaches = {};
const cachePromises = {};

const loadTemplateCache = async (productType = 'windows') => {
  if (templateCaches[productType]) return templateCaches[productType];
  if (cachePromises[productType]) return cachePromises[productType];

  cachePromises[productType] = (async () => {
    try {
      const response = await authFetch('/appearance-templates/');
      const filtered = response.filter(t => t.product_type === productType);
      templateCaches[productType] = filtered.reduce((acc, t) => {
        acc[t.id] = {
          topic: t.topic || 'Other',
          topic_number: t.topic_number || 999
        };
        return acc;
      }, {});
      return templateCaches[productType];
    } catch (err) {
      console.error('Failed to load appearance templates', err);
      templateCaches[productType] = {};
      return templateCaches[productType];
    }
  })();

  return cachePromises[productType];
};

// Products without DB appearance templates (e.g. pleated screens) carry topic /
// topic_number on their JS line templates (docs/templates/lines/<quoteTemplate>.js).
const jsTemplateCaches = {};

const loadJsTemplateTopics = async (quoteTemplate) => {
  if (!jsTemplateCaches[quoteTemplate]) {
    jsTemplateCaches[quoteTemplate] = getTemplates(quoteTemplate).then(lines =>
      (lines || []).reduce((acc, t) => {
        if (t.id && t.topic) {
          acc[t.id] = { topic: t.topic, topic_number: t.topic_number || 999 };
        }
        return acc;
      }, {})
    );
  }
  return jsTemplateCaches[quoteTemplate];
};

const SpecsSection = ({ item, expandAll = false, collapseAll = false, productType: rawProductType = 'windows', promos = [] }) => {
  // Normalize: "window" → "windows", "patiodoor" → "patiodoors"
  const productType = rawProductType.endsWith('s') ? rawProductType : rawProductType + 's';
  const [openChapters, setOpenChapters] = useState({});
  const [templates, setTemplates] = useState({});

  // ←←← MOVE sortedTopics to a ref so it's always available
  const sortedTopicsRef = React.useRef([]);

  // 2. Add this useEffect — this is the magic
  useEffect(() => {
    if (collapseAll) {
      setOpenChapters(prev => {
        const updated = { ...prev };
        sortedTopicsRef.current.forEach(t => updated[t] = false);
        return updated;
      });
    } else if (expandAll) {
      setOpenChapters(prev => {
        const updated = { ...prev };
        sortedTopicsRef.current.forEach(t => updated[t] = true);
        return updated;
      });
    }
  }, [expandAll, collapseAll]);
  

  const toggleChapter = (topic) => {
    setOpenChapters(prev => ({
      ...prev,
      [topic]: !prev[topic]
    }));
  };

  const quoteTemplate = item?.doorData?.quoteTemplate;

  useEffect(() => {
    loadTemplateCache(productType).then(dbTemplates => {
      if (Object.keys(dbTemplates).length === 0 && quoteTemplate) {
        loadJsTemplateTopics(quoteTemplate).then(setTemplates);
      } else {
        setTemplates(dbTemplates);
      }
    });
  }, [productType, quoteTemplate]);

  const grouped = {};
  const standalone = [];


  // Step 1: Filter out the "caaaaaption" line — it's always the very first one
  let visibleLines = (item?.doorData?.calc?.visibleLines || []).filter(
    ([key]) => key !== "caaaaaption"
  );

  // Step 2: Remove redundant topic header lines (e.g. "Main Row:", "Top Row:")
  const topicNamesNormalized = new Set(
    Object.values(templates)
      .map(t => t.topic?.trim().toLowerCase())
      .filter(Boolean)
  );

  visibleLines = visibleLines.filter(([key, val]) => {
    const cleanText = val.html
      ?.replace(/<[^>]*>/g, '')        // strip all HTML tags
      .replace(/&nbsp;/g, ' ')
      .replace(/[✔✓•-]/g, '')          // remove checkmarks and bullets
      .replace(/[:\s]+$/g, '')          // remove trailing colon and spaces
      .trim()
      .toLowerCase();

    const price = val.price?.price ?? null;
    const hasNoPrice = price === null || price === 0;

    const textMatchesTopic = topicNamesNormalized.has(cleanText);

    // Remove only if it's a pure header (matches a topic name + no price)
    return !(textMatchesTopic && hasNoPrice);
  });

  // Now the main loop...
  visibleLines.forEach(([key, val]) => {
    const html = val.html || '';
    const price = val.price?.price ?? 0;
    const original = val.price?.original ?? null;
    const cleanText = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    // Look up the promo by matching the line text against the current promos
    // list — same matcher HandlePromo uses to apply the discount. Done here
    // (instead of relying on a persisted field) so the annotation survives
    // page reloads where the recalc useEffect doesn't fire.
    const promoName = (original != null && original > price)
      ? (getMatchedPromo(promos, cleanText)?.name || null)
      : null;

    // Try full key first, then prefix before first underscore
    const templateId = templates[key] ? key : key.split('_')[0];

    // Priority: 1. Template from cache → 2. val.topic (only if template missing)
    let effectiveTopic = null;
    let effectiveTopicNumber = 999;

    if (templateId && templates[templateId]) {
      effectiveTopic = templates[templateId].topic;
      effectiveTopicNumber = templates[templateId].topic_number;
    } else if (val.topic) {
      effectiveTopic = val.topic;
      effectiveTopicNumber = val.topic_number || 999;
    }

    if (!effectiveTopic) {
      effectiveTopic = 'Other';
    }

    if (cleanText) {
      if (!grouped[effectiveTopic]) {
        grouped[effectiveTopic] = { lines: [], minTopicNumber: Infinity };
      }
      grouped[effectiveTopic].lines.push({
        key, html, price, original, promoName, cleanText,
        topicNumber: effectiveTopicNumber
      });
      grouped[effectiveTopic].minTopicNumber = Math.min(
        grouped[effectiveTopic].minTopicNumber,
        effectiveTopicNumber
      );
    } else if (cleanText) {
      standalone.push({ key, html, price, original, cleanText });
    }
  });

  /*
  const sortedTopics = Object.keys(grouped)
    .sort((a, b) => grouped[a].minTopicNumber - grouped[b].minTopicNumber);
  */
  // ←←← Update the ref after sortedTopics is calculated
  const sortedTopics = Object.keys(grouped)
    .sort((a, b) => grouped[a].minTopicNumber - grouped[b].minTopicNumber);

  // Update ref immediately
  sortedTopicsRef.current = sortedTopics;

  const isOpen = (topic) => openChapters[topic] === true; //initially closed  //initially opened: !== false;

  return (
    <div className="specs-section">
      {sortedTopics
        .filter(topic => grouped[topic].lines.length > 0)
        .map(topic => (
          
          <div key={topic} className="chapter">
            <div
              className="chapter-header"
              onClick={() => toggleChapter(topic)}
            >
              <span className="chapter-toggle">
                {isOpen(topic)
                  ? <ExpandMoreIcon fontSize="small" />
                  : <ChevronRightIcon fontSize="small" />}
              </span>
              <span className="chapter-title">{topic}</span>
            </div>

            {isOpen(topic) && (
              <div className="chapter-content">
                {grouped[topic].lines
                  .sort((a, b) => a.topicNumber - b.topicNumber)
                  .map((line, i) => (
                    <div key={i} className="spec-line">
                      <span
                        className="spec-label"
                        dangerouslySetInnerHTML={{ __html: line.html }}
                      />
                      {line.price > 0 && (
                        <span className="spec-price">
                          {line.original > line.price ? (
                            <>
                              <strong className="price-current">${line.price.toFixed(2)}</strong>
                              <s className="price-original">${line.original.toFixed(2)}</s>
                            </>
                          ) : (
                            <strong className="price-current">${line.price.toFixed(2)}</strong>
                          )}
                        </span>
                      )}
                      {line.promoName && line.original > line.price && (
                        <span className="spec-promo-note">{line.promoName}</span>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
        ))}

      {/* Standalone lines */}
      {standalone.map((line, i) => (
        <div key={i} className="spec-line standalone">
          <span dangerouslySetInnerHTML={{ __html: line.html }} />
          {line.price > 0 && (
            <span className="spec-price">
              {line.original > line.price ? (
                <>
                  <strong className="price-current">${line.price.toFixed(2)}</strong>
                  <s className="price-original">${line.original.toFixed(2)}</s>
                </>
              ) : (
                <strong className="price-current">${line.price.toFixed(2)}</strong>
              )}
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

export default SpecsSection;